import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { isDpFromToken } from "../../../utils/authToken";

import {
  cancelLeaveApi,
  closeLeaveApi,
  DpLeaveDetail,
  DpLeaveSensitive,
  getLeaveApi,
  getLeaveAttachmentBlobApi,
  getLeaveSensitiveApi,
  LEAVE_STATUS_BADGE,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPE_LABELS,
} from "../api/leavesApi";
import DocumentFileButton from "../components/DocumentFileButton";
import EmployeeStatusBadge from "../components/EmployeeStatusBadge";
import LeaveFormModal from "../components/LeaveFormModal";
import LeavePayerBadge from "../components/LeavePayerBadge";
import ReasonModal from "../components/ReasonModal";
import { formatDateOnly, orDash } from "../utils/format";
import { DP_EMPLOYEE_TAB_PAGE, DP_LEAVE_PAGE, DP_LEAVES_PAGE, DP_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="dpDefinition__label">{label}</div>
    <div className="dpDefinition__value">{value}</div>
  </div>
);

export default function LeaveDetailPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_LEAVES_PAGE());
  const { id } = useParams<{ id: string }>();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isDp = isDpFromToken(token);

  const [leave, setLeave] = useState<DpLeaveDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeDate, setCloseDate] = useState("");
  const [canceling, setCanceling] = useState(false);
  const [sensitive, setSensitive] = useState<DpLeaveSensitive | null>(null);
  const [loadingSensitive, setLoadingSensitive] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      setLeave(await getLeaveApi(token, id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o afastamento.");
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    setSensitive(null);
    load();
  }, [load]);

  /** Sob demanda: cada abertura fica registrada no log de acesso (ADR 0006). */
  const openSensitive = async () => {
    if (!token || !id) return;
    setLoadingSensitive(true);
    try {
      setSensitive(await getLeaveSensitiveApi(token, id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os dados de saúde.");
    } finally {
      setLoadingSensitive(false);
    }
  };

  const submitClose = async () => {
    if (!token || !leave) return;
    if (!closeDate) return toast.error("Informe o último dia afastado.");
    try {
      const { warnings } = await closeLeaveApi(token, leave.id, closeDate);
      toast.success("Afastamento encerrado.");
      warnings.forEach((w) => toast.warning(w));
      setClosing(false);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao encerrar.");
    }
  };

  if (!token) return null;

  const auto = leave?.continuations.find((c) => c.isAutoContinuation && c.status !== "CANCELADO") ?? null;
  const manualChildren = leave?.continuations.filter((c) => !c.isAutoContinuation) ?? [];
  const editable = leave && !leave.isAutoContinuation && leave.status !== "CANCELADO";
  const originId = leave?.isAutoContinuation ? leave.parentLeaveId : null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          {loading || !leave ? (
            <div className="dpEmpty">Carregando...</div>
          ) : (
            <>
              <EcoPageHeader
                crumbs={[
                  { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
                  { label: "Afastamentos", onClick: () => navigate(DP_LEAVES_PAGE()) },
                  { label: leave.employee.socialName || leave.employee.fullName },
                ]}
                title={`${LEAVE_TYPE_LABELS[leave.type]} de ${leave.employee.socialName || leave.employee.fullName}`}
                subtitle={
                  <>
                    {LEAVE_STATUS_LABELS[leave.status]} · situação do colaborador:{" "}
                    <EmployeeStatusBadge status={leave.employee.status} />
                  </>
                }
                onBack={goBack}
                backLabel="Voltar"
                actions={
                  <>
                    <span className={`dpBadge ${LEAVE_STATUS_BADGE[leave.status]} dpeHeaderBadge`}>{LEAVE_STATUS_LABELS[leave.status]}</span>
                    <button
                      type="button"
                      className="ecoBtn"
                      onClick={() => navigate(DP_EMPLOYEE_TAB_PAGE(leave.employee.id, "afastamentos"))}
                    >
                      <EcoIcon name="users" size={18} />
                      Ficha do colaborador
                    </button>
                    {editable ? (
                      <>
                        <button type="button" className="ecoBtn" onClick={() => setEditing(true)}>
                          Alterar
                        </button>
                        {leave.status === "ATIVO" ? (
                          <button
                            type="button"
                            className="ecoBtn ecoBtn--primary"
                            onClick={() => {
                              setCloseDate("");
                              setClosing(true);
                            }}
                          >
                            Encerrar (retorno)
                          </button>
                        ) : null}
                        <button type="button" className="ecoBtn ecoBtn--danger" onClick={() => setCanceling(true)}>
                          Cancelar
                        </button>
                      </>
                    ) : null}
                  </>
                }
              />

              {leave.isAutoContinuation ? (
                <p className="dpNotice">
                  Continuação paga pelo INSS, criada pelo sistema a partir do 16º dia. Para alterar datas, encerrar ou
                  cancelar, use o{" "}
                  {originId ? (
                    <button
                      type="button"
                      className="dpBtn dpBtn--secondary dpBtn--small"
                      onClick={() => navigate(DP_LEAVE_PAGE(originId))}
                    >
                      afastamento de origem
                    </button>
                  ) : (
                    "afastamento de origem"
                  )}
                  .
                </p>
              ) : null}

              <section className="dpSection">
                <h2 className="dpSection__title">Período</h2>
                <div className="dpDefinitionGrid">
                  <Field label="Início" value={formatDateOnly(leave.startDate)} />
                  <Field
                    label="Último dia afastado"
                    value={leave.eventEndDate ? formatDateOnly(leave.eventEndDate) : "Em aberto"}
                  />
                  <Field label="Dias" value={leave.eventDays ?? "—"} />
                  <Field label="Previsão de retorno" value={formatDateOnly(leave.expectedEndDate)} />
                  <Field label="Quem paga" value={<LeavePayerBadge payer={leave.payer} />} />
                  <Field label="Dias pagos pela empresa" value={leave.employerPaidDays} />
                  {leave.catNumber || leave.type === "ACIDENTE_TRABALHO" ? (
                    <Field
                      label="CAT"
                      value={leave.catNumber ? `${leave.catNumber} · ${formatDateOnly(leave.catIssuedAt)}` : "Não registrada"}
                    />
                  ) : null}
                  <Field label="Lançado por" value={`${leave.createdBy.nomeCompleto} em ${formatDateOnly(leave.createdAt)}`} />
                </div>
                {leave.notes ? <p className="dpFormHint">Observações: {orDash(leave.notes)}</p> : null}
                {leave.cancelReason ? <p className="dpNotice">Cancelado: {leave.cancelReason}</p> : null}
              </section>

              {auto || leave.parentLeave || manualChildren.length > 0 ? (
                <section className="dpSection">
                  <h2 className="dpSection__title">Cadeia do afastamento</h2>
                  <div className="dpTimeline">
                    {leave.parentLeave ? (
                      <div className="dpTimelineItem" style={{ cursor: "pointer" }} onClick={() => navigate(DP_LEAVE_PAGE(leave.parentLeave!.id))}>
                        <div className="dpTimelineItem__period">
                          Continua: {LEAVE_TYPE_LABELS[leave.parentLeave.type]} de {formatDateOnly(leave.parentLeave.startDate)}
                        </div>
                      </div>
                    ) : null}
                    <div className="dpTimelineItem is-current">
                      <div className="dpTimelineItem__header">
                        <span className="dpTimelineItem__period">
                          {formatDateOnly(leave.startDate)} a {leave.endDate ? formatDateOnly(leave.endDate) : "em aberto"} ·{" "}
                          {leave.days ?? "—"} dia(s)
                        </span>
                        <LeavePayerBadge payer={leave.payer} />
                      </div>
                    </div>
                    {auto ? (
                      <div className="dpTimelineItem">
                        <div className="dpTimelineItem__header">
                          <span className="dpTimelineItem__period">
                            {LEAVE_TYPE_LABELS[auto.type]}: {formatDateOnly(auto.startDate)} a{" "}
                            {auto.endDate ? formatDateOnly(auto.endDate) : "em aberto"} · {auto.days ?? "—"} dia(s)
                          </span>
                          <LeavePayerBadge payer={auto.payer} />
                        </div>
                        <span className="dpMuted">A partir do 16º dia — criado pelo sistema.</span>
                      </div>
                    ) : null}
                    {manualChildren.map((child) => (
                      <div
                        key={child.id}
                        className="dpTimelineItem"
                        style={{ cursor: "pointer", ...(child.status === "CANCELADO" ? { opacity: 0.6 } : {}) }}
                        onClick={() => navigate(DP_LEAVE_PAGE(child.id))}
                      >
                        <div className="dpTimelineItem__period">
                          Continuado por: {LEAVE_TYPE_LABELS[child.type]} de {formatDateOnly(child.startDate)}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              {isDp && !leave.isAutoContinuation ? (
                <section className="dpSection">
                  <h2 className="dpSection__title">Dados de saúde</h2>
                  <div className="dpSensitiveBlock">
                    <span className="dpFormHint" style={{ margin: 0 }}>
                      Dado pessoal sensível (LGPD art. 11). Só o papel DP acessa, e cada acesso fica registrado com
                      data, usuário e IP.
                    </span>
                    {sensitive ? (
                      <>
                        <div className="dpDefinitionGrid">
                          <Field label="CID" value={orDash(sensitive.cid)} />
                          <Field label="Médico" value={orDash(sensitive.physicianName)} />
                          <Field label="CRM" value={orDash(sensitive.physicianCrm)} />
                        </div>
                        <div className="dpTable__actions">
                          {sensitive.hasAttachment ? (
                            <DocumentFileButton
                              token={token}
                              fetchBlob={() => getLeaveAttachmentBlobApi(token, leave.id, "attachment")}
                              fileName={sensitive.attachmentFileName}
                              label="Abrir atestado"
                            />
                          ) : (
                            <span className="dpFormHint">Sem atestado anexado.</span>
                          )}
                          {sensitive.hasCat ? (
                            <DocumentFileButton
                              token={token}
                              fetchBlob={() => getLeaveAttachmentBlobApi(token, leave.id, "cat")}
                              fileName={sensitive.catFileName}
                              label="Abrir CAT"
                            />
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <div>
                        <button className="dpBtn dpBtn--secondary" onClick={openSensitive} disabled={loadingSensitive}>
                          {loadingSensitive ? "Carregando..." : "Ver dados de saúde (acesso registrado)"}
                        </button>
                      </div>
                    )}
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>
      </main>

      {editing && leave ? (
        <LeaveFormModal
          token={token}
          employeeId={leave.employeeId}
          isDp={isDp}
          editing={leave}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSensitive(null);
            load();
          }}
        />
      ) : null}

      {closing && leave ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Encerrar afastamento</h2>
              <button className="dpModal__close" onClick={() => setClosing(false)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <p className="dpNotice">
                Informe o último dia afastado — o retorno ao trabalho é no dia seguinte. A divisão entre empresa e INSS é
                recalculada com esta data.
              </p>
              <div className="dpFormGroup">
                <label htmlFor="closeDate">Último dia afastado *</label>
                <input
                  id="closeDate"
                  type="date"
                  className="dpInput"
                  value={closeDate}
                  onChange={(e) => setCloseDate(e.target.value)}
                />
              </div>
            </div>
            <footer className="dpModal__footer">
              <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setClosing(false)}>
                Voltar
              </button>
              <button type="button" className="dpBtn dpBtn--primary" onClick={submitClose}>
                Encerrar
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {canceling && leave ? (
        <ReasonModal
          title="Cancelar afastamento"
          description="Use para lançamento errado. A continuação do INSS e os afastamentos que continuam este também são cancelados."
          confirmLabel="Cancelar afastamento"
          onClose={() => setCanceling(false)}
          onConfirm={async (reason) => {
            await cancelLeaveApi(token, leave.id, reason);
            toast.success("Afastamento cancelado.");
            setCanceling(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
